// @ts-check
import React, { useState } from "react"
import ContentLayoutHeader from "../../../../components/layouts/ContentLayoutHeader.layout"
import Card from "../../../../components/atoms/Card.atom"
import Column from "../../../../components/atoms/table/Column.atom"
import VacationDayVariantChip from "../../../../components/molecules/VacationDayVariantChip.molecule"
import { ReactComponent as BucketIcon } from "../../../../design/assests/images/icons/glass_cup.svg"
import {
  VACATION_DAY_TYPES,
  VACATION_DAYS_VARIANTS,
  VACATION_SCHEMAS,
} from "../../../../consts/modules/vacations.consts"
import { formatDateWithShortMonth } from "../../../../utils/date.utils"
import DataTable from "../../../../components/atoms/table/DataTable.atom"
import { getTeamMemberVacationHistory } from "../../../../services/vacation.service"
import VacationFlag from "../../../../components/molecules/VacationFlag.molecule"
import TeamMemberVacationsBucketTable from "../common/TeamMemberVacationsBucketTable.controller"
import Tooltip from "../../../../components/atoms/Tooltip.atom"
import Modal from "../../../../components/atoms/Modal.atom"
import { useParams } from "react-router-dom"
import { clsx } from "../../../../utils/strings.utils"
import VacationHistoryRowExpansion from "./VacationHIstoryRowExpansion.controller"
import { useSession } from "../../../../hooks/auth/useSession.hook"
import useMount from "../../../../hooks/useMount"

/** @typedef {import("../../../../services/vacation.service.types").TeamMemberVacationHistoryData} VacationHistory */
/** @typedef {import("../../../../services/vacation.service.types").TeamMemberVacationHistoryMemberData} TeamMemberData */

export default function VacationsHistory({ isAdminView = true }) {
  const [expandedRows, setExpandedRows] = useState({})
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [showBucketModal, setShowBucketModal] = useState(false)
  const [selectedVacation, setSelectedVacation] = useState(
    /** @type {VacationHistory | null} */
    (null)
  )
  const [data, setData] = useState(
    /** @type {VacationHistory[]} */
    ([])
  )
  const [teamMember, setTeamMember] = useState(
    /** @type {TeamMemberData | null} */
    (null)
  )
  const { teamMemberId: paramsUserId } = useParams()
  const {user} = useSession()

  const teamMemberId = isAdminView ? paramsUserId : user.id

  const usingForeignSchema =
    teamMember?.vacationSchemaName === VACATION_SCHEMAS.INTERNATIONAL

  useMount(() => {
    if (!teamMemberId) return
    setIsLoadingData(true)
    getTeamMemberVacationHistory(teamMemberId)
      .then((resp) => {
        if (resp.success && resp.result) {
          setData(resp.result.vacationsData)
          setTeamMember(resp.result.teamMember)
          resp.result.vacationsData.forEach((vacation) => {
            if (vacation.current) {
              setExpandedRows({
                [vacation.year]: true,
              })
            }
          })
        }
      })
      .finally(() => {
        setIsLoadingData(false)
      })
  })

  return (
    <div id="vacations-history-container">
      <Modal
        visible={showBucketModal}
        onHide={() => setShowBucketModal(false)}
        closeOnEscape
        header={() => {
          return (
            <div className="flex gap-3 align-items-center">
              <BucketIcon className="text-info" width={32} height={32} />
              <div className="flex flex-column">
                <h2 className="font-bold text-3xl">Resumen de cubeta</h2>
              </div>
            </div>
          )
        }}
        style={{ width: "624px", maxWidth: "90vw" }}
        draggable={false}
        className="pb-2"
      >
        <TeamMemberVacationsBucketTable
          teamMemberId={teamMember?.id}
          workAnniversary={teamMember?.workAnniversary || undefined}
          periodStartDate={selectedVacation?.periodStart}
          periodEndDate={selectedVacation?.periodEnd}
        />
      </Modal>
      <ContentLayoutHeader
        title={
          <>
            <p className="mb-0">Historial de vacaciones</p>
            {isAdminView && (
              <div className="flex gap-3 align-items-center">
                <div className="flex gap-2 align-items-center text-xl font-normal">
                  <VacationFlag schemaTitle={teamMember?.vacationSchemaName} />
                    {teamMember?.fullName}
                </div>
                <div className="text-xl flex gap-2 align-items-center font-normal">
                  <i className="pi pi-calendar text-sm"></i>
                    {teamMember?.yearsWorked} año
                    {(teamMember?.yearsWorked || 0) > 1 ? "s" : ""}
                </div>
              </div>
            )}
          </>
        }
        goBack={isAdminView}
      />

      <Card className="vacations-report-card shadow-none p-1">
        <DataTable
          value={data}
          className="vacations-history-table hoverable-table-row"
          rowExpansionTemplate={(data) => (
            <VacationHistoryRowExpansion
              data={data}
              isUsingForeignSchema={usingForeignSchema}
            />
          )}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          responsiveLayout="scroll"
          scrollable
          scrollHeight="100%"
          dataKey="year"
          loading={isLoadingData}
          loadingConfig={{ qtyOfRows: 5 }}
        >
          <Column
            frozen
            alignFrozen="left"
            expander={true}
            className="expander-column"
          />
          <Column
            frozen
            alignFrozen="left"
            field="year"
            header="Año"
            className="year-column"
            headerClassName="year-header"
            body={(data) => (
              <p className="year flex gap-2 text-lg">
                {data.year}
                {data.current && (
                  <div className="current-year-chip px-2 py-1 border-round-3xl flex line-height-1 align-items-center">
                    Actual
                  </div>
                )}
              </p>
            )}
          />
          <Column
            field="periodStart"
            header="Periodo"
            className="period-column"
            body={(data) => (
              <p className="period">
                {formatDateWithShortMonth(data.periodStart)} -{" "}
                {formatDateWithShortMonth(data.periodEnd)}
              </p>
            )}
          />
          <Column className="period-filler-cell-1" />
          <Column
            header="Días solicitados"
            className="days-taken-column"
            body={(node) => {
              const isFuturePeriod = node.year > (teamMember?.yearsWorked || 0)

              return (
                <div className="flex gap-4 align-items-center">
                  {!isFuturePeriod && (
                    <Tooltip
                      target={`.bucket-days-resume-chip-${node.year}`}
                      content="Click para ver resumen de cubeta"
                    />
                  )}
                  {!usingForeignSchema &&
                    (node.daysTaken.bucket > 0 ||
                      node.daysRequested.bucket > 0) && (
                      <div className="taken-days-chip-wrapper flex gap-2 align-items-center h-full">
                        <VacationDayVariantChip
                          className={clsx(
                            `bucket-days-resume-chip bucket-days-resume-chip-${node.year}`,
                            !isFuturePeriod && "cursor-pointer"
                          )}
                          onClick={() => {
                            if (isFuturePeriod) return
                            setSelectedVacation(node)
                            setShowBucketModal(true)
                          }}
                          variant={VACATION_DAYS_VARIANTS.LONG}
                          dayType={VACATION_DAY_TYPES.BUCKET}
                        />
                        <p className="amount">{node.daysTaken.bucket}</p>
                      </div>
                    )}
                  {(node.daysTaken.plus > 0 || node.daysRequested.plus > 0) && (
                    <div className="taken-days-chip-wrapper flex gap-2 align-items-center h-full">
                      <VacationDayVariantChip
                        variant={VACATION_DAYS_VARIANTS.LONG}
                        dayType={VACATION_DAY_TYPES.PLUS}
                      />
                      <p className="amount">{node.daysTaken.plus}</p>
                    </div>
                  )}
                  {(node.daysTaken.accorded > 0 ||
                    node.daysRequested.accorded > 0) && (
                    <div className="taken-days-chip-wrapper flex gap-2 align-items-center h-full">
                      <VacationDayVariantChip
                        variant={VACATION_DAYS_VARIANTS.LONG}
                        dayType={VACATION_DAY_TYPES.ACCORDED}
                      />
                      <p className="amount">{node.daysTaken.accorded}</p>
                    </div>
                  )}
                  {(node.daysTaken.additional > 0 ||
                    node.daysRequested.additional > 0) && (
                    <div className="taken-days-chip-wrapper flex gap-2 align-items-center h-full">
                      <VacationDayVariantChip
                        variant={VACATION_DAYS_VARIANTS.LONG}
                        dayType={VACATION_DAY_TYPES.EXTRA}
                      />
                      <p className="amount">{node.daysTaken.additional}</p>
                    </div>
                  )}
                </div>
              )
            }}
          />

          <Column className="taken-days-filler-cell-1" />
          <Column className="taken-days-filler-cell-2" />
        </DataTable>
      </Card>
    </div>
  )
}
